import { DateTime } from 'luxon';
import { T, always, cond, equals } from 'ramda';
import React from 'react';
import styled from 'styled-components';

import BaseCard from '@ui/atoms/BaseCard';
import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import Eyebrow from '@ui/atoms/Eyebrow';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import Pill from '@ui/atoms/Pill';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';
import padded from '@ui/styled/Padded';
import spaced from '@ui/styled/Spaced';

import {
	IInvoice,
	IInvoiceKind,
	IInvoiceLineItem,
	InvoiceStatus,
	PaymentStatus,
} from '@hooks/useMemberInvoice';

import { exists } from '@utils/general';
import { centsToDollars } from '@utils/money';

import ExclamationQuotes from '@assets/vectors/exclamation-quotes.inline.svg';

import theme from '@theme/FolxTheme';

export interface INewBillCardWrapperContent {
	disclaimerCopy: string;
	payNowLabel: string;
}

interface IBillCardProps {
	bill: IInvoice;
	wrapperContent: INewBillCardWrapperContent;
	className?: string;
	handleClick?: () => void;
}

interface IHeaderProps {
	amountPaid: string;
	amountRemaining: string;
	canceled: boolean;
	status: InvoiceStatus;
	billedTo?: string;
}

type ToDisplay = <T = unknown>(fn: (arg: T) => string, arg: T | null) => string;

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const toDisplay: ToDisplay = (fn, arg) => (exists(arg) ? fn(arg!) : '-');

const formatDate = (date?: DateTime, options?: Intl.DateTimeFormatOptions) =>
	date ? date.toLocaleString(options) : '';

const formatDateRange = (startDate: DateTime, endDate: DateTime) =>
	`${startDate.toLocaleString()} - ${endDate.toLocaleString()}`;

const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const PaddedColumn = padded<IFlexColumn>(FullWidthColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);
const SpacedRow = spaced(FlexRow);
const AlignedRow = aligned(FlexRow);
const FullWidthEyebrow = fullWidth(Eyebrow);
const AlignedColumn = aligned(SpacedColumn);

const HeaderRow = styled(SpacedRow)`
	position: relative;
	align-items: center;
	justify-content: space-between;
`;

const StatusPill = styled(Pill)`
	min-width: fit-content;
	padding: ${theme.spacing.xs} ${theme.spacing.md};
`;

const LineItemCard = styled(BaseCard)`
	padding: ${theme.spacing.xs} 0;
	min-width: 20rem;
`;

const DisclaimerCard = styled(BaseCard)`
	display: flex;
	flex-flow: row;
	div {
		flex-basis: fit-content;
	}
`;

const HeaderStatusPill: Record<IHeaderProps['status'], React.ReactNode> = {
	[InvoiceStatus.paid]: <StatusPill color="green" variant="pill" title="Paid" />,
	[InvoiceStatus.open]: <StatusPill color="lightRed" variant="pill" title="Failed" />,
	[InvoiceStatus.uncollectible]: <StatusPill color="lightRed" variant="pill" title="Failed" />,
	[InvoiceStatus.void]: <StatusPill color="lightBeige" variant="pill" title="Canceled" />,
};

const LineItemRow = ({ label, value }: { label: string; value: string }) => (
	<AlignedRow justifyContent="space-between">
		<BasicText variant="pill">{label}</BasicText>
		<BasicText variant="pillText">{value}</BasicText>
	</AlignedRow>
);

const Header = ({ status, amountPaid, amountRemaining, billedTo, canceled }: IHeaderProps) => (
	<header>
		<HeaderRow marginBottom="sm">
			<AlignedColumn alignItems="flex-start">
				<BasicText variant="field">
					{status === InvoiceStatus.paid ? amountPaid : amountRemaining}
				</BasicText>
				{status === InvoiceStatus.paid && billedTo && (
					<BasicText variant="fineprint">{billedTo}</BasicText>
				)}
			</AlignedColumn>
			{canceled ? (
				<StatusPill color="lightBeige" variant="pill" title="Canceled" />
			) : (
				HeaderStatusPill[status]
			)}
		</HeaderRow>
		<FullWidthEyebrow size="xs" underline={true} weight="normal" color="placeholderGray" />
	</header>
);

const BillLineItem = ({
	createdDate,
	item,
	paidDate,
	status,
	kind,
}: {
	createdDate: string;
	item: IInvoiceLineItem;
	paidDate: string;
	status: InvoiceStatus;
	kind: IInvoiceKind;
}) => {
	const startDate = item.period.start;
	const endDate = item.period.end;

	const lineItemHeader = cond([
		[
			equals(IInvoiceKind.labs),
			always(
				<LineItemRow
					label="Ordered"
					value={
						exists(startDate)
							? formatDate(startDate, {
									month: 'long',
									day: 'numeric',
							  })
							: '-'
					}
				/>
			),
		],
		[
			equals(IInvoiceKind.membership),
			always(
				<SpacedColumn gap="sm">
					{status === InvoiceStatus.paid && <LineItemRow label="Paid" value={paidDate} />}
					<LineItemRow label="Created" value={createdDate} />
				</SpacedColumn>
			),
		],
		[T, always(<LineItemRow label="Created" value={createdDate} />)],
	])(kind);

	const lineItemTopRow = cond([
		[equals(IInvoiceKind.labs), always(<LineItemRow label="Lab" value={item.description} />)],
		[
			equals(IInvoiceKind.membership),
			always(<LineItemRow label="Membership period" value={formatDateRange(startDate, endDate)} />),
		],
		[T, always(<LineItemRow label="Description" value={item.description} />)],
	])(kind);

	return (
		<SpacedColumn gap="md">
			{lineItemHeader}
			<LineItemCard bgColor="lightBeige" borderless>
				<FlexColumn gap="sm">
					{lineItemTopRow}
					<LineItemRow label="Cost" value={toDisplay(centsToDollars, item.amount)} />
				</FlexColumn>
			</LineItemCard>
			<Eyebrow size="xs" underline={true} weight="normal" color="placeholderGray" />
		</SpacedColumn>
	);
};

const NewBillCard = ({
	bill,
	className,
	handleClick = () => {
		return;
	},
	wrapperContent,
}: IBillCardProps) => {
	const total = toDisplay(centsToDollars, bill.total);
	const paidDate = toDisplay(formatDate, bill?.paidDate);
	const createdDate = toDisplay(formatDate, bill?.created);
	const amountPaid = toDisplay(centsToDollars, bill?.amountPaid);
	const amountRemaining = toDisplay(centsToDollars, bill?.amountRemaining);

	const billedTo = bill?.paymentMethod?.card
		? `Billed to ${bill.paymentMethod.card.brand}...${bill.paymentMethod.card.last4}`
		: undefined;

	const status = bill.status;
	const canceled = bill?.paymentStatus === PaymentStatus.canceled;

	const { disclaimerCopy, payNowLabel } = wrapperContent;

	return (
		<BaseCard bgColor="white" radius="md" borderless shadow className={className}>
			<PaddedColumn gap="sm" padding="md">
				<Header
					status={status}
					canceled={canceled}
					amountPaid={amountPaid}
					amountRemaining={amountRemaining}
					billedTo={billedTo}
				/>
				<SpacedColumn gap="lg">
					{bill.lineItems.map((item, index) => (
						<BillLineItem
							status={status}
							createdDate={createdDate}
							paidDate={paidDate}
							item={item}
							kind={bill.kind}
							key={index}
						/>
					))}
					<AlignedRow gap="md" justifyContent="flex-end">
						<BasicText variant="small">Total</BasicText>
						<BasicText variant="smallText">{total}</BasicText>
					</AlignedRow>
					{status !== InvoiceStatus.paid && (
						<>
							<Button
								variant="secondary"
								newTab
								label={payNowLabel}
								onClick={() => {
									window.open(bill?.hostedInvoiceUrl ?? '');
									handleClick();
								}}
							/>
							<DisclaimerCard borderless padding="smmd" radius="xl" bgColor="lightRed">
								<ExclamationQuotes />
								<WYSIWYGContent
									textVariant="pillText"
									dangerouslySetInnerHTML={{ __html: disclaimerCopy }}
								/>
							</DisclaimerCard>
						</>
					)}
				</SpacedColumn>
			</PaddedColumn>
		</BaseCard>
	);
};

export default styled(NewBillCard)``;
